import { SignIn } from "@clerk/clerk-react";
import { Link, Outlet, useLocation } from "@tanstack/react-router";
import {
  IconCloudUpload,
  IconFileExport,
  IconLayoutDashboard,
  IconPlugConnected,
  IconTemplate,
  type Icon,
} from "@tabler/icons-react";

import { AUTH_DISABLED, useAppAuth } from "@/lib/auth";

type NavItem = { path: string; label: string; icon: Icon };

const NAV_ITEMS: readonly NavItem[] = [
  { path: "/", label: "Upload files", icon: IconCloudUpload },
  { path: "/dashboard", label: "Dashboard", icon: IconLayoutDashboard },
  { path: "/templates", label: "Templates", icon: IconTemplate },
  { path: "/export", label: "Export", icon: IconFileExport },
] as const;

export function RootLayout() {
  const { isSignedIn, isLoaded } = useAppAuth();
  const location = useLocation();

  if (!isLoaded) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg">
        <div className="text-text-2 text-sm">Loading…</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg">
        <SignIn />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside className="w-[220px] min-w-[220px] bg-bg-2 border-r border-border flex flex-col">
        <div className="px-5 py-[18px] pb-[14px] border-b border-border flex items-center gap-2.5">
          <div className="w-7 h-7 bg-accent rounded flex items-center justify-center font-mono text-[13px] font-medium text-bg">
            V
          </div>
          <div>
            <div className="text-sm font-semibold text-text tracking-tight">The Validator</div>
            <div className="text-[10px] text-text-2 mt-0.5">Invoice reconciliation</div>
          </div>
        </div>

        <nav className="flex-1 pt-2 pb-4" aria-label="Main navigation">
          <div className="px-3 pt-4 pb-2 text-[10px] font-medium uppercase tracking-[.08em] text-text-3">
            Reconciliation
          </div>
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-current={isActive ? "page" : undefined}
                className={[
                  "flex items-center gap-2.5 px-3 py-2 rounded text-[13px] mx-2 my-0.5 border transition-all",
                  isActive
                    ? "text-text bg-bg-3 border-border"
                    : "text-text-2 border-transparent hover:text-text hover:bg-bg-3",
                ].join(" ")}
              >
                <Icon size={16} className="w-[18px]" aria-hidden />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto p-4 border-t border-border">
          <div className="text-[11px] text-text-2 mb-1">Last run: —</div>
          <div className="text-[11px] text-text-3">v0.1.0</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-12 bg-bg-2 border-b border-border flex items-center px-6 flex-shrink-0">
          <span className="text-sm font-medium text-text">
            {NAV_ITEMS.find((n) => n.path === location.pathname)?.label ?? "The Validator"}
          </span>
          {AUTH_DISABLED && (
            <span
              role="status"
              className="ml-auto flex items-center gap-1.5 rounded border border-accent bg-accent-dim px-2.5 py-1 text-[13px] text-accent"
            >
              <IconPlugConnected size={14} aria-hidden />
              Local mode: sign-in disabled
            </span>
          )}
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
